import type { ReactNode } from "react";
import { Fragment } from "react";
import { Link } from "react-router-dom";
import type { Media } from "@zivin-labs/site-content";
import { Figure } from "./Figure";
import { ROUTES } from "../lib/routes";

export interface BreadcrumbItem {
  label: string;
  /** Route or anchor href; omit for the last (current) crumb. */
  to?: string;
}

interface PageHeroProps {
  eyebrow: string;
  title: string;
  lead?: string;
  crumbs?: BreadcrumbItem[];
  /** Placeholder label for the image column. Pass `null` for no image at all. */
  image?: string | null;
  variant?: string;
  heroMedia?: Media | null;
  /**
   * Content for the second column, in place of the image. A page with nothing
   * to illustrate can put something useful there instead of a grey box; with
   * neither, the hero collapses to one wide column.
   */
  aside?: ReactNode;
  /** Call-to-action buttons, rendered under the lead. */
  actions?: ReactNode;
  /**
   * Only meaningful with `image={null}` (no aside either): the title is
   * capped at 18ch site-wide so it doesn't stretch edge-to-edge on the
   * normal two-column hero, but that cap makes a short title wrap
   * unnecessarily once the hero is already full-width with nothing beside
   * it to fill. Opt-in (default false) so the other imageless heroes on the
   * site keep their current wrapping unless a page asks for this.
   */
  fullWidthTitle?: boolean;
}

/** Standard page hero used at the top of every secondary page. */
export function PageHero({
  eyebrow,
  title,
  lead,
  crumbs = [],
  image = "PAGE HERO",
  variant = "",
  heroMedia,
  aside,
  actions,
  fullWidthTitle = false,
}: PageHeroProps) {
  const showFigure = !aside && image !== null;
  const cls =
    "page-hero" + (variant ? " " + variant : "") + (fullWidthTitle ? " hero-title-full" : "");
  return (
    <section className={cls}>
      <div className="container">
        <div className="breadcrumb">
          <Link to={ROUTES.home}>Home</Link>
          {crumbs.map((c, i) => (
            <Fragment key={i}>
              <span>/</span>
              {c.to ? <Link to={c.to}>{c.label}</Link> : <span>{c.label}</span>}
            </Fragment>
          ))}
        </div>
        <div className={"page-hero-grid" + (showFigure || aside ? "" : " is-single")}>
          <div>
            <div className="kicker eyebrow" style={{ color: "var(--gold-400)" }}>
              {eyebrow}
            </div>
            <h1>{title}</h1>
            {lead && <p className="lead">{lead}</p>}
            {actions && <div className="page-hero-actions">{actions}</div>}
          </div>
          {showFigure && (
            <div>
              <Figure
                media={heroMedia}
                fallbackLabel={image ?? "PAGE HERO"}
                kind="dark"
                aspect="4/3"
                priority
              />
            </div>
          )}
          {aside && <div className="page-hero-aside">{aside}</div>}
        </div>
      </div>
    </section>
  );
}

export interface SideSection {
  title: string;
  items: Array<{ label: string; to?: string; active?: boolean }>;
}

interface PageLayoutProps {
  side: SideSection[];
  children: ReactNode;
}

/** Two-column page body: sticky side nav + content. */
export function PageLayout({ side, children }: PageLayoutProps) {
  return (
    <div className="container">
      <div className="page-layout">
        <aside className="page-side">
          {side.map((sect, i) => (
            <div key={i} style={{ marginBottom: 32 }}>
              <h4>{sect.title}</h4>
              <ul>
                {sect.items.map((it, j) => {
                  const cls = it.active ? "active" : "";
                  if (!it.to) {
                    return (
                      <li key={j}>
                        <a href="#" className={cls} onClick={(e) => e.preventDefault()}>
                          {it.label}
                        </a>
                      </li>
                    );
                  }
                  if (it.to.startsWith("#") || it.to.startsWith("http")) {
                    return (
                      <li key={j}>
                        <a href={it.to} className={cls}>
                          {it.label}
                        </a>
                      </li>
                    );
                  }
                  return (
                    <li key={j}>
                      <Link to={it.to} className={cls}>
                        {it.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </aside>
        <div className="page-content">{children}</div>
      </div>
    </div>
  );
}
